import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AttachedCompany } from 'Backend/users/getAttachedCompanies';

import { getCompanyStatus, getCurrentUserEmail, getCurrentUserId, setCurrentCompanyId } from 'Lib/Utils';
import classes from './SwitchForm.module.css';
import AuthPageWrapper from 'Components/Auth/AuthPageWrapper/AuthPageWrapper';
import StatusChip from 'Components/UI_Library/StatusChips';
import TextInput from 'Components/UI_Library/FormElements/TextInput';
import { mapPermissionsToRoleName } from 'Pages/Team/utils/mapPermissionsToRoleName';
import { useGetAttachedCompanies } from 'Backend/users/getAttachedCompanies';
import Button from 'Components/UI_Library/FormElements/Button';
import { useRemoveUserFromCompany } from 'Backend/user-management/removeUserFromCompany';
import { useActivateUserInCompany } from 'Backend/user-management/activateUserInCompany';
import LoadingBar from 'Components/UI_Library/LoadingIndicators/LoadingBar';
import { Notification } from 'Components/UI_Library/Notification';
import { logger } from 'utils/logger';
import { setAttachedCompanies } from 'Backend/users/utils/attachedCompanies';
import { isValidUrl } from 'utils/validation/isValidUrl';

const PAYTRON_DOMAIN = window.location.origin;

const isPaytronEmail = getCurrentUserEmail()?.includes('@paytron.com');

/** Adds companyUserStatus attribute to list of attached companies */
const transformCompanies = (companies: AttachedCompany[], type: 'ACTIVE' | 'PENDING') =>
    companies.map((company) => ({ companyUserStatus: type, ...company }));

const SwitchForm = () => {
    const navigate = useNavigate();
    const {
        data: getCompaniesResult,
        isSuccess: successGetCompanies,
        isLoading: isLoadingGetCompanies,
        isFetching: isFetchingGetCompanies,
        refetch: refetchCompanies,
    } = useGetAttachedCompanies({ status: 'ACTIVE' });
    const listPermission = transformCompanies(getCompaniesResult?.data || [], 'ACTIVE');

    // Bypassing invites: Pending companies
    const {
        data: getPendingCompaniesResult,
        isSuccess: successGetPendingCompanies,
        isFetching: isFetchingGetPendingCompanies,
        refetch: refetchPendingCompanies,
    } = useGetAttachedCompanies({
        status: 'PENDING',
    });
    const pendingListPermission = transformCompanies(getPendingCompaniesResult?.data || [], 'PENDING');
    const [search, setSearch] = useState('');

    const [allCompanies, setAllCompanies] = useState<ReturnType<typeof transformCompanies>>([]);

    // REMOVE YOURSELF FROM COMPANY
    const {
        mutate: removeUserFromCompany,
        error: errorRemovingUserFromCompany,
        isSuccess: successRemoveUserFromCompany,
        isLoading: isLoadingRemoveUserFromCompany,
    } = useRemoveUserFromCompany();
    const [companyIdToRemove, setCompanyIdToRemove] = useState('');

    // ADD/ACTIVATE YOURSELF IN COMPANY
    const {
        mutate: activateUserInCompany,
        error: errorActivatingUserInCompany,
        isSuccess: successActivatingUserInCompany,
        isLoading: isLoadingActivatingUserInCompany,
    } = useActivateUserInCompany();
    const [companyIdToAdd, setCompanyIdToAdd] = useState('');

    // Notifications
    const [notification, setNotification] = useState('');
    const [notificationType, setNotificationType] = useState<'success' | 'error'>();

    const [isCheckingNavigationPath, setIsCheckingNavigationPath] = useState(true);

    // get encoded redirect path from URL
    const searchParams = new URLSearchParams(window.location.search);
    const redirectParam = searchParams.get('redirect');
    const redirect = redirectParam ? decodeURIComponent(redirectParam) : '';

    useEffect(() => {
        setAllCompanies([...listPermission, ...pendingListPermission]);
    }, [successGetCompanies, successGetPendingCompanies]);

    const handleAccept = (selectedCompanyId: string) => {
        setCurrentCompanyId(selectedCompanyId);
        const status = getCompanyStatus();
        const domainAndRoute = `${PAYTRON_DOMAIN}${redirect || ''}`;
        const redirectURL = isValidUrl(domainAndRoute) ? domainAndRoute : '/';

        console.log('From switch page, setting current company ID as: ', selectedCompanyId);
        console.log('Previous route: ', redirectURL);
        switch (status) {
            case 'APPROVED - CARDS ONLY':
            case 'APPROVED':
            case 'APPROVED - NO KYC':
            case 'PROCESSING':
                navigate(redirectURL);
                break;
            case 'FAILED':
                if (isPaytronEmail) {
                    // INSPECT: what is intention of this? Paytron employees can access FAILED company?
                    navigate(redirectURL);
                } else {
                    navigate('/signout');
                }
                break;
            default:
                navigate(`/onboarding`);
                break;
        }
    };

    useEffect(() => {
        if (
            successGetCompanies &&
            successGetPendingCompanies &&
            !isFetchingGetCompanies &&
            !isFetchingGetPendingCompanies
        ) {
            const companyId = getCompaniesResult.data?.[0]?.companyId;
            const numberOfCompanies = getCompaniesResult.data?.length ?? 0;
            const numberOfPendingCompanies = getPendingCompaniesResult.data?.length;

            if (numberOfCompanies === 1 && numberOfPendingCompanies === 0 && companyId) {
                handleAccept(companyId);
            } else {
                setIsCheckingNavigationPath(false);
            }
        }
    }, [
        successGetCompanies,
        successGetPendingCompanies,
        isFetchingGetCompanies,
        isFetchingGetPendingCompanies,
        getCompaniesResult,
        getPendingCompaniesResult,
    ]);

    const statusMap = (status?: string) => {
        switch (status) {
            case 'PENDING':
            case 'PROCESSING':
                return 'ONBOARDING';
            case 'APPROVED - CARDS ONLY':
                return 'CARDS ONLY';
            case 'APPROVED - NO KYC':
                return 'REFERRAL ONLY';
            case 'APPROVED':
                return 'APPROVED';
            case 'FAILED':
                return 'FAILED';
            default:
                return 'ERROR';
        }
    };

    const searchFilter = (item: AttachedCompany) => {
        if (search?.length > 0) {
            const valueToSearch = `${item?.companyName?.toLowerCase()} ${statusMap(
                item?.companyStatus
            )?.toLowerCase()} ${item?.type?.toLowerCase()}`;
            return valueToSearch.indexOf(search?.toLowerCase()) > -1 || false;
        }
        return true;
    };

    // Bypassing invites
    const archiveYourselfFromCompany = async (companyId: string) => {
        setCompanyIdToRemove(companyId);
        removeUserFromCompany({ companyId, userId: getCurrentUserId() });
    };

    useEffect(() => {
        if (successRemoveUserFromCompany) {
            handleNotification('Invite has been successfully declined', 'success');
            updatePermissions();
        }

        if (errorRemovingUserFromCompany) {
            handleNotification(
                errorRemovingUserFromCompany.detail || 'An error occurred declining the invite.',
                'error'
            );
        }
    }, [errorRemovingUserFromCompany, successRemoveUserFromCompany]);

    useEffect(() => {
        if (!isLoadingRemoveUserFromCompany) {
            setCompanyIdToRemove('');
        }
    }, [isLoadingRemoveUserFromCompany]);

    const addYourselfToCompany = async (companyId: string) => {
        setCompanyIdToAdd(companyId);
        activateUserInCompany({
            companyId,
        });
    };

    useEffect(() => {
        if (successActivatingUserInCompany) {
            handleNotification('Invite has been successfully accepted', 'success');
            updatePermissions();
        }

        if (errorActivatingUserInCompany) {
            handleNotification(
                errorActivatingUserInCompany.detail || 'An error occurred accepting the invite.',
                'error'
            );
        }
    }, [errorActivatingUserInCompany, successActivatingUserInCompany]);

    useEffect(() => {
        if (!isLoadingActivatingUserInCompany) {
            setCompanyIdToAdd('');
        }
    }, [isLoadingActivatingUserInCompany]);

    /** Update local storage and list of companies */
    const updatePermissions = async () => {
        const { data: attachedPendingCompaniesResult } = await refetchPendingCompanies();
        const pendingCompanies = attachedPendingCompaniesResult?.data || [];
        const { data: attachedCompanies } = await refetchCompanies();
        const currentPermissions = attachedCompanies?.data || [];
        setAttachedCompanies(currentPermissions);
        const updatedAllCompanies = [
            ...transformCompanies(pendingCompanies, 'PENDING'),
            ...transformCompanies(currentPermissions, 'ACTIVE'),
        ];
        setAllCompanies(updatedAllCompanies);

        // If the user has no companies, redirect to welcome
        if (!updatedAllCompanies.length) {
            navigate('/welcome');
        }
    };

    const handleNotification = (message: string, type: 'success' | 'error', timeout = 3000) => {
        setNotification(message);
        setNotificationType(type);
        setTimeout(() => {
            setNotification('');
            setNotificationType(undefined);
        }, timeout);
    };

    return (
        <AuthPageWrapper>
            <div
                className={classes.container}
                style={{ justifyContent: allCompanies?.length > 5 ? 'flex-start' : 'center' }}
            >
                <h2 className={classes.chooseAccount}>Select account to enter</h2>
                {notification && (
                    <Notification
                        message={notification}
                        type={notificationType}
                        extraClass={classes.notification}
                        noCloseBtn
                    />
                )}
                {(isCheckingNavigationPath || isLoadingGetCompanies) && <LoadingBar />}
                <div className={classes.selectSection}>
                    {allCompanies?.length > 5 && (
                        <TextInput
                            placeholder="Search for an account..."
                            value={search}
                            onChange={(e: { target: { value: string } }) => setSearch(e.target.value)}
                            style={{ maxHeight: '35px' }}
                            containerStyle={{ padding: '10px 20px 15px 15px' }}
                        />
                    )}
                    {allCompanies
                        ?.filter(searchFilter)
                        ?.sort((a, b) => (a?.companyName || '').localeCompare(b?.companyName || ''))
                        .map((item) => (
                            <div
                                className={`${classes.company} ${
                                    item?.companyUserStatus === 'PENDING' && classes.pendingCompany
                                }`}
                                key={item.companyId}
                                onClick={() =>
                                    item.companyUserStatus !== 'ACTIVE' /* eslint-disable-line no-nested-ternary */
                                        ? logger.warn('Selected an inactive company', { company: item })
                                        : !item.companyId
                                        ? logger.error('Selected company without companyId', { company: item })
                                        : handleAccept(item.companyId)
                                }
                            >
                                {item.companyName ? (
                                    <div className={classes.companyName}>
                                        <span>{item.companyName}</span>
                                        <span>
                                            {mapPermissionsToRoleName({
                                                permissions: item.permissions,
                                                roleName: item.type,
                                            })}{' '}
                                            role
                                            {item?.companyUserStatus === 'PENDING' && <span>&nbsp;(Invitation)</span>}
                                        </span>
                                    </div>
                                ) : (
                                    <div className={classes.emptyBadge}>UNNAMED ACCOUNT</div>
                                )}
                                {item.companyUserStatus === 'ACTIVE' ? (
                                    <>
                                        <div className={classes.status}>
                                            {item.companyStatus !== 'APPROVED' && (
                                                <StatusChip
                                                    filled
                                                    status={
                                                        item.companyStatus
                                                            ? `${statusMap(item.companyStatus)}`
                                                            : 'ERROR'
                                                    }
                                                />
                                            )}
                                        </div>

                                        <div className={classes.arrow}>
                                            <span />
                                            <span />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {item?.companyId && (
                                            <div className={classes.acceptButtons}>
                                                <Button
                                                    type="cancel"
                                                    text="Decline"
                                                    onClick={() =>
                                                        item.companyId && archiveYourselfFromCompany(item.companyId)
                                                    }
                                                    loading={companyIdToRemove === item.companyId}
                                                    disabled={
                                                        isLoadingRemoveUserFromCompany ||
                                                        isLoadingActivatingUserInCompany
                                                    }
                                                />
                                                <Button
                                                    type="white"
                                                    text="Accept"
                                                    onClick={() =>
                                                        item.companyId && addYourselfToCompany(item.companyId)
                                                    }
                                                    loading={companyIdToAdd === item.companyId}
                                                    disabled={
                                                        isLoadingActivatingUserInCompany ||
                                                        isLoadingRemoveUserFromCompany
                                                    }
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                </div>
            </div>
        </AuthPageWrapper>
    );
};

export default SwitchForm;
